import { useState, useEffect, useCallback } from "react";
import { ClipboardList, Play, Loader2, Sparkles, Trash2, CheckCircle, XCircle, ArrowLeft, RotateCw } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { SkeletonList } from "./Skeleton";

/**
 * Per-organ quiz section embedded inside each Organ page.
 * - Student: lists quizzes for this organ, click Play to take inline.
 * - Admin: extra "AI Generate" + Delete buttons.
 */
export const OrganQuizzes = ({ apiUrl, organ, organIndex, studentName, lang = "ms", isAdmin = false }) => {
  const isEn = lang === "en";
  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [active, setActive] = useState(null); // active quiz being taken
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [numQuestions, setNumQuestions] = useState(5);

  const organTitle = organ?.title || "";
  const organId = organ?.id;

  const fetchQuizzes = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${apiUrl}/quizzes`);
      const filtered = (res.data || []).filter(
        (q) => q.organ === organTitle || q.organ === organId
      );
      setQuizzes(filtered);
    } catch {
      setQuizzes([]);
    } finally {
      setLoading(false);
    }
  }, [apiUrl, organTitle, organId]);

  useEffect(() => {
    fetchQuizzes();
  }, [fetchQuizzes]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      await axios.post(`${apiUrl}/generate-quiz`, {
        organ_id: organId,
        num_questions: numQuestions,
      });
      toast.success(isEn ? "AI quiz generated!" : "Quiz AI berjaya dijana!");
      await fetchQuizzes();
    } catch (e) {
      toast.error(e.response?.data?.detail || (isEn ? "Failed to generate quiz" : "Gagal menjana quiz"));
    } finally {
      setGenerating(false);
    }
  };

  const handleDelete = async (quiz) => {
    if (!window.confirm(isEn ? `Delete "${quiz.title}"?` : `Padam "${quiz.title}"?`)) return;
    try {
      await axios.delete(`${apiUrl}/quizzes/${quiz.id}`);
      toast.success(isEn ? "Quiz deleted" : "Quiz dipadam");
      await fetchQuizzes();
    } catch (e) {
      toast.error(e.response?.data?.detail || (isEn ? "Failed to delete" : "Gagal padam"));
    }
  };

  const startQuiz = (quiz) => {
    setActive(quiz);
    setAnswers({});
    setResult(null);
  };

  const exitQuiz = () => {
    setActive(null);
    setAnswers({});
    setResult(null);
  };

  const handleSubmit = async () => {
    if (!active) return;
    if (Object.keys(answers).length < active.questions.length) {
      toast.error(isEn ? "Please answer all questions" : "Sila jawab semua soalan");
      return;
    }
    setSubmitting(true);
    try {
      // Backend expects answers as dict keyed by string index
      const answersDict = {};
      active.questions.forEach((_, i) => {
        answersDict[String(i)] = answers[i] ?? -1;
      });
      const payload = {
        answers: answersDict,
        student_name: studentName || "",
      };
      const res = await axios.post(`${apiUrl}/quizzes/${active.id}/submit`, payload);
      setResult(res.data);
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : (isEn ? "Failed to submit" : "Gagal hantar");
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const retryQuiz = () => {
    setAnswers({});
    setResult(null);
  };

  // Active quiz view ----------------------------------------------------------
  if (active) {
    return (
      <div className="dash-card" data-testid={`organ-${organIndex}-quiz-active`}>
        <div className="dash-card-header">
          <h3>
            <ClipboardList size={16} className="text-[#0A0A0A]" />
            {active.title}
          </h3>
          <button
            onClick={exitQuiz}
            className="flex items-center gap-1 text-xs font-medium text-[rgba(0,0,0,0.55)] hover:text-[#0A0A0A] px-2 py-1 rounded-md hover:bg-[#F7F7F7]"
            data-testid="quiz-exit-btn"
          >
            <ArrowLeft size={12} /> {isEn ? "Back" : "Kembali"}
          </button>
        </div>

        <div className="p-5 space-y-5">
          {result ? (
            <div className="text-center py-6" data-testid="quiz-result">
              <div className={`w-20 h-20 rounded-full mx-auto flex items-center justify-center mb-4 ${result.score >= 50 ? "bg-green-50" : "bg-red-50"}`}>
                {result.score >= 50 ? <CheckCircle size={40} className="text-green-600" /> : <XCircle size={40} className="text-red-500" />}
              </div>
              <p className="text-3xl font-bold text-[#0A0A0A]">
                {result.correct} / {result.total}
              </p>
              <p className="text-sm text-[rgba(0,0,0,0.55)] mt-1">
                {result.score}% {isEn ? "correct" : "betul"}
              </p>
              <button
                onClick={retryQuiz}
                className="mt-5 inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm font-semibold text-white bg-[#2563EB] hover:bg-[#1d4ed8]"
                data-testid="quiz-retry-btn"
              >
                <RotateCw size={14} /> {isEn ? "Try again" : "Cuba semula"}
              </button>
            </div>
          ) : (
            <>
              {active.questions.map((q, qi) => (
                <div key={qi} className="space-y-2" data-testid={`quiz-question-${qi}`}>
                  <p className="text-sm font-semibold text-[#0A0A0A]">
                    {qi + 1}. {q.question}
                  </p>
                  <div className="space-y-1.5">
                    {q.options.map((opt, oi) => (
                      <label
                        key={oi}
                        className={`flex items-center gap-2 px-3 py-2 rounded-xl border cursor-pointer transition-colors ${
                          answers[qi] === oi
                            ? "border-[#2563EB] bg-[#EFF6FF]"
                            : "border-[rgba(0,0,0,0.08)] hover:border-[#0A0A0A]"
                        }`}
                        data-testid={`quiz-option-${qi}-${oi}`}
                      >
                        <input
                          type="radio"
                          name={`q-${qi}`}
                          value={oi}
                          checked={answers[qi] === oi}
                          onChange={() => setAnswers((prev) => ({ ...prev, [qi]: oi }))}
                          className="w-4 h-4 accent-[#2563EB]"
                        />
                        <span className="text-sm text-[#0A0A0A]">{opt}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="w-full flex items-center justify-center gap-2 px-5 py-3 rounded-full text-sm font-semibold text-white bg-[#0A0A0A] hover:bg-[#1a1a1a] disabled:opacity-60"
                data-testid="quiz-submit-btn"
              >
                {submitting ? <Loader2 size={16} className="animate-spin" /> : null}
                {isEn ? "Submit answers" : "Hantar jawapan"}
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  // List view ---------------------------------------------------------------
  // Hide entirely for non-admin when no quizzes (clean UX for students)
  if (!isAdmin && !loading && quizzes.length === 0) return null;

  return (
    <div className="dash-card" data-testid={`organ-${organIndex}-quizzes`}>
      <div className="dash-card-header">
        <h3>
          <ClipboardList size={16} className="text-[#0A0A0A]" />
          {isEn ? `Quiz - Organ ${organIndex}` : `Quiz Organ ${organIndex}`}
        </h3>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400">
            {quizzes.length} {isEn ? "quiz" : "quiz"}
          </span>
          {isAdmin && (
            <>
              <select
                value={numQuestions}
                onChange={(e) => setNumQuestions(parseInt(e.target.value))}
                className="text-[11px] px-2 py-1 rounded-md border border-[rgba(0,0,0,0.1)] bg-white"
                data-testid={`organ-${organIndex}-quiz-numq`}
                title={isEn ? "Number of questions" : "Bilangan soalan"}
              >
                {[3, 5, 7, 10, 15, 20, 25, 30].map((n) => (
                  <option key={n} value={n}>
                    {n} {isEn ? "Q" : "S"}
                  </option>
                ))}
              </select>
              <button
                onClick={handleGenerate}
                disabled={generating}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold text-white bg-[#2563EB] hover:bg-[#1d4ed8] disabled:opacity-60"
                data-testid={`organ-${organIndex}-quiz-generate-btn`}
              >
                {generating ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                {generating
                  ? isEn ? "Generating…" : "Menjana…"
                  : isEn ? "AI Generate" : "Jana AI"}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="p-3">
        {loading ? (
          <SkeletonList count={2} />
        ) : quizzes.length === 0 ? (
          <div className="text-center py-6 text-xs text-[rgba(0,0,0,0.4)] italic">
            {isAdmin
              ? (isEn ? `No quizzes yet. Click "AI Generate" to create one from Organ ${organIndex} content.` : `Belum ada quiz. Klik "Jana AI" untuk cipta dari kandungan Organ ${organIndex}.`)
              : (isEn ? "No quizzes available yet." : "Belum ada quiz.")}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {quizzes.map((q) => (
              <div
                key={q.id}
                className="flex items-center gap-3 px-3 py-3 rounded-xl border border-[rgba(0,0,0,0.06)] hover:border-[#0A0A0A] hover:shadow-[0_4px_12px_rgba(0,0,0,0.04)] transition-all duration-150"
                data-testid="quiz-item"
              >
                <div className="w-10 h-10 rounded-xl bg-[#F7F7F7] flex items-center justify-center flex-shrink-0">
                  <ClipboardList size={18} className="text-[#0A0A0A]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-[#0A0A0A] truncate" title={q.title}>
                    {q.title}
                  </p>
                  <p className="text-[10px] text-[rgba(0,0,0,0.4)]">
                    {q.questions?.length || 0} {isEn ? "questions" : "soalan"}
                    {q.ai_generated ? ` · ${isEn ? "AI" : "AI"}` : ""}
                  </p>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    onClick={() => startQuiz(q)}
                    className="flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] font-semibold text-white bg-[#0A0A0A] hover:bg-[#1a1a1a]"
                    data-testid="quiz-play-btn"
                  >
                    <Play size={11} /> {isEn ? "Play" : "Mula"}
                  </button>
                  {isAdmin && (
                    <button
                      onClick={() => handleDelete(q)}
                      className="w-8 h-8 rounded-full hover:bg-red-50 flex items-center justify-center"
                      title={isEn ? "Delete" : "Padam"}
                      data-testid="quiz-delete-btn"
                    >
                      <Trash2 size={14} className="text-red-500" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
